// /app/frontend/src/components/admin/FreightCouponsAdmin.js
import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiTag, FiPlus, FiCopy, FiCheck, FiX, FiRefreshCw,
  FiLink, FiTrash2, FiShare2, FiGlobe, FiShoppingBag,
  FiUsers, FiCalendar
} from 'react-icons/fi';
import api from '@/services/api';
import { formatCurrency } from '@/utils/formatters';

const FreightCouponsAdmin = () => {
  const [coupons, setCoupons] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [filter, setFilter] = useState('all'); // all, global, store
  
  // Form states
  const [newCode, setNewCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(100);
  const [maxUses, setMaxUses] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(30);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [couponsRes, storesRes] = await Promise.all([
        api.get('/coupons/freight'),
        api.get('/admin/stores')
      ]);
      setCoupons(couponsRes.data.coupons || []);
      setStores(storesRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    setCreateError('');
    setCreateSuccess('');

    if (!newCode.trim()) {
      setCreateError('Digite um código para o cupom');
      return;
    }

    if (newCode.trim().length < 4) {
      setCreateError('O código deve ter pelo menos 4 caracteres');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        code: newCode.trim().toUpperCase(),
        discount_percent: discountPercent,
        max_uses: maxUses ? parseInt(maxUses) : null,
        expires_in_days: expiresInDays
      };

      // Se selecionou uma loja específica
      if (selectedStoreId) {
        payload.store_id = selectedStoreId;
      }

      const response = await api.post('/coupons/freight', payload);

      setCreateSuccess(`Cupom ${response.data.code} criado com sucesso!`);
      setTimeout(() => {
        setShowCreateModal(false);
        resetForm();
        loadData();
      }, 1500);
    } catch (error) {
      setCreateError(error.response?.data?.detail || 'Erro ao criar cupom');
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewCode('');
    setMaxUses('');
    setDiscountPercent(100);
    setExpiresInDays(30);
    setSelectedStoreId('');
    setCreateSuccess('');
    setCreateError('');
  };

  const handleToggleStatus = async (couponId, currentStatus) => {
    try {
      await api.put(`/coupons/freight/${couponId}`, {
        is_active: !currentStatus
      });
      loadData();
    } catch (error) {
      console.error('Erro ao alterar status:', error);
    }
  };

  const handleDelete = async (couponId) => {
    if (!confirm('Desativar este cupom?')) return;
    try {
      await api.delete(`/coupons/freight/${couponId}`);
      loadData();
    } catch (error) {
      console.error('Erro ao excluir cupom:', error);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(type);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const getDiscountBadgeColor = (percent) => {
    if (percent === 100) return 'bg-green-500 text-white';
    if (percent >= 70) return 'bg-blue-500 text-white';
    if (percent >= 50) return 'bg-yellow-500 text-white';
    return 'bg-gray-500 text-white';
  };

  const filteredCoupons = coupons.filter(c => {
    if (filter === 'global') return c.is_global;
    if (filter === 'store') return !c.is_global;
    return true;
  });

  // Estatísticas
  const stats = {
    total: coupons.length,
    global: coupons.filter(c => c.is_global).length,
    store: coupons.filter(c => !c.is_global).length,
    active: coupons.filter(c => c.is_active).length,
    totalUses: coupons.reduce((sum, c) => sum + (c.uses_count || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="freight-coupons-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FiTag className="text-red-500" />
            Cupons de Desconto no Frete
          </h3>
          <p className="text-sm text-gray-500">
            Gerencie cupons globais e veja cupons das lojas
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          data-testid="create-coupon-admin-btn"
        >
          <FiPlus />
          Novo Cupom Global
        </button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
          <p className="text-sm text-gray-500">Total de Cupons</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <p className="text-2xl font-bold text-blue-600">{stats.global}</p>
          <p className="text-sm text-gray-500">Cupons Globais</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <p className="text-2xl font-bold text-purple-600">{stats.store}</p>
          <p className="text-sm text-gray-500">Cupons de Lojas</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          <p className="text-sm text-gray-500">Ativos</p>
        </div>
        <div className="bg-white p-4 rounded-xl shadow-sm border">
          <p className="text-2xl font-bold text-orange-600">{stats.totalUses}</p>
          <p className="text-sm text-gray-500">Usos Totais</p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-2">
        {[
          { key: 'all', label: 'Todos', icon: FiTag },
          { key: 'global', label: 'Globais (Admin)', icon: FiGlobe },
          { key: 'store', label: 'De Lojas', icon: FiShoppingBag }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              filter === f.key
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <f.icon size={16} />
            {f.label}
          </button>
        ))}
        <button
          onClick={loadData}
          className="ml-auto flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm"
        >
          <FiRefreshCw size={14} />
          Atualizar
        </button>
      </div>

      {/* Lista de Cupons */}
      {filteredCoupons.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <FiTag className="mx-auto text-4xl text-gray-400 mb-3" />
          <p className="text-gray-500">Nenhum cupom encontrado</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Código</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Desconto</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Tipo</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Usos</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Validade</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredCoupons.map((coupon) => {
                const isExpired = new Date(coupon.expires_at) < new Date();
                const isExhausted = coupon.max_uses && coupon.uses_count >= coupon.max_uses;

                return (
                  <tr key={coupon.id} className={`hover:bg-gray-50 ${(!coupon.is_active || isExpired || isExhausted) ? 'opacity-60' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-gray-800">{coupon.code}</span>
                        <button
                          onClick={() => copyToClipboard(coupon.code, `code-${coupon.code}`)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          {copiedCode === `code-${coupon.code}` ? (
                            <FiCheck className="text-green-500" size={14} />
                          ) : (
                            <FiCopy size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-bold ${getDiscountBadgeColor(coupon.discount_percent)}`}>
                        {coupon.discount_percent}% OFF
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {coupon.is_global ? (
                        <span className="flex items-center gap-1 text-blue-600 text-sm">
                          <FiGlobe size={14} />
                          Global
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-purple-600 text-sm">
                          <FiShoppingBag size={14} />
                          {coupon.store_name || 'Loja'}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {coupon.uses_count}{coupon.max_uses ? ` / ${coupon.max_uses}` : ''}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(coupon.expires_at).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-4 py-3">
                      {isExpired ? (
                        <span className="px-2 py-1 bg-red-100 text-red-600 rounded text-xs">Expirado</span>
                      ) : isExhausted ? (
                        <span className="px-2 py-1 bg-orange-100 text-orange-600 rounded text-xs">Esgotado</span>
                      ) : coupon.is_active ? (
                        <span className="px-2 py-1 bg-green-100 text-green-600 rounded text-xs">Ativo</span>
                      ) : (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Inativo</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => copyToClipboard(coupon.link, `link-${coupon.code}`)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded"
                          title="Copiar link"
                        >
                          {copiedCode === `link-${coupon.code}` ? <FiCheck size={16} /> : <FiLink size={16} />}
                        </button>
                        <button
                          onClick={() => handleToggleStatus(coupon.id, coupon.is_active)}
                          className={`p-1.5 rounded ${coupon.is_active ? 'text-yellow-500 hover:bg-yellow-50' : 'text-green-500 hover:bg-green-50'}`}
                          title={coupon.is_active ? 'Pausar' : 'Ativar'}
                        >
                          {coupon.is_active ? '⏸' : '▶'}
                        </button>
                        <button
                          onClick={() => handleDelete(coupon.id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                          title="Excluir"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Criar Cupom */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 shadow-xl" data-testid="create-coupon-admin-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FiGlobe className="text-blue-500" />
                Criar Cupom de Frete
              </h3>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-blue-700">
                <strong>Cupom Global:</strong> Válido em todas as lojas. A plataforma assume o custo do desconto.
              </p>
            </div>

            {createError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-red-700">{createError}</p>
              </div>
            )}

            {createSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-700 flex items-center gap-1">
                  <FiCheck /> {createSuccess}
                </p>
              </div>
            )}

            <div className="space-y-4">
              {/* Código */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Código do Cupom *
                </label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                  placeholder="Ex: FRETEGRATIS"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 font-mono"
                  maxLength={20}
                />
              </div>

              {/* Tipo de cupom */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo do Cupom
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSelectedStoreId('')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      !selectedStoreId
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FiGlobe className={`mx-auto mb-1 ${!selectedStoreId ? 'text-blue-500' : 'text-gray-400'}`} size={24} />
                    <p className="font-medium text-sm">Global</p>
                    <p className="text-xs text-gray-500">Todas as lojas</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedStoreId('select')}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      selectedStoreId
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <FiShoppingBag className={`mx-auto mb-1 ${selectedStoreId ? 'text-purple-500' : 'text-gray-400'}`} size={24} />
                    <p className="font-medium text-sm">Loja Específica</p>
                    <p className="text-xs text-gray-500">Escolher loja</p>
                  </button>
                </div>

                {selectedStoreId && (
                  <select
                    value={selectedStoreId === 'select' ? '' : selectedStoreId}
                    onChange={(e) => setSelectedStoreId(e.target.value)}
                    className="w-full mt-3 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Selecione uma loja...</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Desconto */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Porcentagem de Desconto *
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {[25, 50, 70, 100].map((percent) => (
                    <button
                      key={percent}
                      type="button"
                      onClick={() => setDiscountPercent(percent)}
                      className={`py-2 rounded-lg font-bold transition-all ${
                        discountPercent === percent
                          ? 'bg-red-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {percent}%
                    </button>
                  ))}
                </div>
              </div>

              {/* Limite e Validade */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Limite de Usos
                  </label>
                  <input
                    type="number"
                    value={maxUses}
                    onChange={(e) => setMaxUses(e.target.value)}
                    placeholder="Ilimitado"
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                    min="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Validade (dias)
                  </label>
                  <select
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(parseInt(e.target.value))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-red-500"
                  >
                    <option value={7}>7 dias</option>
                    <option value={15}>15 dias</option>
                    <option value={30}>30 dias</option>
                    <option value={60}>60 dias</option>
                    <option value={90}>90 dias</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                disabled={creating}
              >
                Cancelar
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !newCode.trim() || (selectedStoreId === 'select')}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {creating ? (
                  <>
                    <FiRefreshCw className="animate-spin" />
                    Criando...
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Criar Cupom
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreightCouponsAdmin;
